import { ArrowUpRight } from 'lucide-react'
import { useFraudUser } from '@/lib/api/fraud-queries'
import { useEvent } from '@/lib/api/queries'
import { cn } from '@/lib/utils'
import { CardBrand } from './card-brand'
import { PILL, ROW, TD, TH, failureLabel, fmtDateTime, kindLabel, moneyFormatter, relativeTime } from './format'
import { Banner, DecisionChip, OutcomeChip, ScoreBadge, SignalList, Spinner } from './shared'

const DECISIONS_SHOWN = 5

/**
 * The attendee's risk at this event, as the user modal's Risk tab: the
 * score and what the next charge gets, why, the cards on file with their
 * trust, block and verification state, and the latest decisions. The full
 * page (evidence, charges, transfers, chargebacks, the trust and block
 * forms) is one click away.
 */
export function AttendeeRiskTab({ userId, eventId, onOpenFull }: {
  userId: number
  eventId: number
  onOpenFull: () => void
}) {
  const { data, isLoading, error } = useFraudUser(eventId, userId)
  const { data: event } = useEvent(eventId)
  const money = moneyFormatter(event?.currency)

  if (error) {
    return (error as { status?: number }).status === 404
      ? <p className="text-sm text-neutral-500 dark:text-neutral-400">No card activity at this event yet. Risk is scored once a card is charged or added here.</p>
      : <Banner kind="error">Could not load risk: {error.message}</Banner>
  }
  if (isLoading || !data) return <div className="flex items-center justify-center py-12"><Spinner /></div>

  const decisions = data.assessments.slice(0, DECISIONS_SHOWN)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <ScoreBadge score={data.score} />
            <span className="text-sm text-neutral-600 dark:text-neutral-400">next charge</span>
            <DecisionChip decision={data.decision} />
            {data.trusted && <span className={cn(PILL, 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400')}>Trusted</span>}
          </div>
          {data.block && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              Blocked ({data.block.scope}, {data.block.reason}{data.block.freeze_spending ? ', spending frozen' : ', cards only'}) since {fmtDateTime(data.block.created_at)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onOpenFull}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline dark:text-blue-400"
        >
          Full risk profile <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">Why this score</h3>
        {data.signals.length === 0
          ? <p className="text-sm text-neutral-500 dark:text-neutral-400">Nothing scored.</p>
          : <SignalList signals={data.signals} />}
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">Cards on file</h3>
        {data.cards_on_file.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No cards recorded at this organization's events.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead><tr className="border-b border-neutral-200 dark:border-neutral-800">
                <th className={TH}>Card</th><th className={TH}>State</th><th className={TH}>Added</th>
              </tr></thead>
              <tbody>
                {data.cards_on_file.map((c) => (
                  <tr key={c.id} className={cn(ROW, c.disabled_at && 'opacity-50')}>
                    <td className={TD}><CardBrand brand={c.brand} last4={c.last4} wallet={c.wallet} /></td>
                    <td className={TD}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {c.blocked && <span className={cn(PILL, 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400')}>Blocked</span>}
                        {c.verification_state === 'failed' && (
                          <span className={cn(PILL, 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400')} title={c.verification_failure_code || undefined}>
                            Verification failed{c.verification_failure_code ? ` · ${failureLabel(c.verification_failure_code)}` : ''}
                          </span>
                        )}
                        {c.trusted && <span className={cn(PILL, 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400')}>Trusted</span>}
                        {c.wallet
                          ? <span className="text-xs text-neutral-500">Wallet</span>
                          : c.three_ds_verified_at
                            ? <span className="text-xs text-neutral-500">3DS verified</span>
                            : <span className="text-xs text-amber-600 dark:text-amber-400">Not 3DS verified</span>}
                        {c.disabled_at && <span className="text-xs text-neutral-500">removed</span>}
                      </div>
                    </td>
                    <td className={TD}>{relativeTime(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">Latest decisions</h3>
        {decisions.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">None yet.</p>
        ) : (
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {decisions.map((a) => (
              <li key={a.id} className="py-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <DecisionChip decision={a.decision} />
                  <OutcomeChip outcome={a.outcome} />
                  <span className="text-neutral-600 dark:text-neutral-400">{kindLabel(a.kind)} · {money(a.amount)} · score {a.score}</span>
                  {a.failure_code && <span className="font-mono text-xs text-red-600">{a.failure_code}</span>}
                  <span className="ml-auto text-xs text-neutral-500">{relativeTime(a.created_at)}</span>
                </div>
                {a.signals.length > 0 && <div className="mt-1.5"><SignalList signals={a.signals} compact /></div>}
              </li>
            ))}
            {data.assessments.length > decisions.length && (
              <li className="py-2 text-xs text-neutral-500">{data.assessments.length - decisions.length} more on the full profile</li>
            )}
          </ul>
        )}
      </section>
    </div>
  )
}
